import { Container, Grid } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Stack } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { search, stop, update } from "../../redux/actions/staff";
import Footer from "../common/Footer";
import Header from "../common/Header";

export default function StaffInformationManagement() {
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [gridData, setGridData] = useState([]);
  const dispatch = useDispatch();
  const staffs = useSelector(state => state.staff.row);
  const [selectedRows, setSelectedRows] = useState([]);

  const pendingActionRef = useRef(null);

  useEffect(() => {
    setGridData(staffs);
  }, [staffs]);

  useEffect(() => {
    if (pendingActionRef.current) {
      const actionType = pendingActionRef.current;
      pendingActionRef.current = null;

      const selectedData = selectedRows.map(rowId => gridData.find(row => row.id === rowId));

      if (actionType === 'update') {
        dispatch(update(selectedData));
      } else if (actionType === 'stop') {
        dispatch(stop(selectedData));
      }
    }
  }, [gridData, selectedRows, dispatch]);

  // 検索実行
  const searchSubmit = request => {
    dispatch(search(request));
    setGridData(staffs);
  };

  const handleSelectionModelChange = selectionModel => {
    setSelectedRows(selectionModel);
  };

  const handleGridDataChange = params => {
    const { id, field, value } = params;

    setGridData(prevData => {
      const newData = prevData.map(row => {
        if (row.id === id) {
          return { ...row, [field]: value };
        }
        return row;
      });
      return newData;
    });
  };

  // Function to blur the active input element within the DataGrid's editing cell
  const blurActiveInput = () => {
    const editCellInput = document.querySelector(".MuiDataGrid-cell--editing .MuiInputBase-input");
    if (editCellInput) {
      editCellInput.blur();
      return true;
    }
    return false;
  };

  const handleSubmitAction = (actionType) => {
    const wasCellBlurred = blurActiveInput();

    if (wasCellBlurred) {
      pendingActionRef.current = actionType;
    } else {
      const selectedData = selectedRows.map(rowId => gridData.find(row => row.id === rowId));
      if (actionType === 'update') {
        dispatch(update(selectedData));
      } else if (actionType === 'stop') {
        dispatch(stop(selectedData));
      }
    }
  };

  const columns = [
    { field: "fmNumber", headerName: "教職員番号", width: 132, editable: true, headerClassName: "headerColor" },
    { field: "belongs", headerName: "所属", editable: true, width: 130, headerClassName: "headerColor" },
    { field: "name", headerName: "氏名", editable: true, width: 180, headerClassName: "headerColor" },
    {
      field: "password",
      headerName: "pass",
      editable: true,
      width: 200,
      headerClassName: "headerColor",
      renderCell: params => {
        const password = params.value;
        const maskedPassword = "*".repeat(password.length);
        return (
          <span>
            {maskedPassword}
          </span>
        );
      },
      flex: 1
    },
    { field: "email", headerName: "メールアドレス", editable: true, width: 226, headerClassName: "headerColor", flex: 1 },
    {
      field: "endDate",
      headerName: "有効期限",
      type: "dateTime",
      editable: true,
      width: 100,
      headerClassName: "headerColor",
      renderCell: params =>
        <div>
          {params.value
            ? new Date(params.value).toLocaleString("ja-JP", { year: "numeric", month: "2-digit", day: "2-digit" })
            : ""}
        </div>
    }
  ];

  return (
    <Box>
      <Header />
      <Container maxWidth="xl">
        <Box
          sx={{
            marginTop: 4,
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <Typography component="h3" variant="h7" align="left">
            教職員情報管理
          </Typography>
        </Box>
        <Box
          sx={{
            marginTop: 2,
            marginBottom: 2,
            alignItems: "center",
            justifyContent: "center",
            paddingLeft: 2
          }}
        >
          <Typography component="h4" variant="h7" align="left">
            いずれか入力された情報で教職員を検索します。
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            bgcolor: "#fff9c4",
            width: "97%",
            marginTop: 2,
            marginBottom: 2,
            marginLeft: 2
          }}
        >
          <Box
            sx={{
              mt: 1,
              alignItems: "left",
              justifyContent: "left"
            }}
          >
            <Grid container sx={{ flexGrow: 1 }} spacing={1} alignItems="flex-start" justifyContent="flex-start" textAlign={"left"}>
              <Grid item xs={4} lg={3}>
                <Box
                  sx={{
                    mt: 1,
                    alignItems: "flex-start"
                  }}
                >
                  <FormControl sx={{ mr: 3, ml: 10 }}>
                    <Typography variant="h7" align={"left"}>
                      教職員アカウント番号
                    </Typography>
                  </FormControl>
                </Box>
              </Grid>
              <Grid item xs={8} lg={9} marginTop={1}>
                <FormControl>
                  <TextField
                    id="outlined-basic"
                    size="small"
                    variant="outlined"
                    {...register("serialNumber", {
                      pattern: { value: /^[0-9]+$/, message: "通し番号は数字で入力してください。" }
                    })}
                    sx={{ background: "white" }}
                    style={{ width: "300px" }}
                  />
                  {errors.serialNumber &&
                    <span className="error-msg">
                      {errors.serialNumber.message}
                    </span>}
                </FormControl>                
              </Grid>
              <Grid item xs={4} lg={3}>
                <Box
                  sx={{
                    alignItems: "left",
                    justifyContent: "left"
                  }}
                >
                  <FormControl sx={{ mr: 3, ml: 10 }}>
                    <Typography variant="h7" align="left">
                      所属
                    </Typography>
                  </FormControl>
                </Box>
              </Grid>
              <Grid item xs={8} lg={9}>
                <FormControl>
                  <TextField
                    id="outlined-basic"
                    size="small"
                    variant="outlined"
                    {...register("belongs")}
                    sx={{ background: "white" }}
                    style={{ width: "300px" }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4} lg={3}>
                <Box
                  sx={{
                    alignItems: "left",
                    justifyContent: "left"
                  }}
                >
                  <FormControl sx={{ mr: 3, ml: 10 }}>
                    <Typography variant="h7" align="left">
                      氏名
                    </Typography>
                  </FormControl>
                </Box>
              </Grid>
              <Grid item xs={8} lg={9}>
                <FormControl>
                  <TextField
                    id="outlined-basic"
                    size="small"
                    variant="outlined"
                    {...register("name")}
                    sx={{ background: "white" }}
                    style={{ width: "300px" }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4} lg={3} marginTop={1}>
                <Box
                  sx={{
                    mt: 1,
                    alignItems: "left",
                    justifyContent: "left"
                  }}
                >
                  <FormControl sx={{ mr: 3, ml: 10, alignItems: "center", justifyContent: "center" }}>
                    <Typography variant="h7" align="left">
                      有効期限
                    </Typography>
                  </FormControl>
                </Box>
              </Grid>
              <Grid item xs={8} lg={9} marginTop={1}>
                <FormControl>
                  <RadioGroup row aria-labelledby="demo-row-radio-buttons-group-label" name="row-radio-buttons-group">
                    <FormControlLabel
                      {...register("endDateFlg", { required: "いずれかの有効期限を選択してください。" })}
                      value="1"
                      control={<Radio />}
                      label="有効のみ"
                    />
                    <FormControlLabel
                      {...register("endDateFlg", { required: "いずれかの有効期限を選択してください。" })}
                      value="2"
                      control={<Radio />}
                      label="無効のみ"
                    />
                    <FormControlLabel
                      {...register("endDateFlg", { required: "いずれかの有効期限を選択してください。" })}
                      value="0"
                      control={<Radio />}
                      label="すべて"
                    />
                  </RadioGroup>
                  {errors.endDateFlg &&
                    <span className="error-msg">
                      {errors.endDateFlg.message}
                    </span>}
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Box sx={{ alignItems: "right", justifyContent: "right", display: "flex", mr: 7 }}>
          <Container maxWidth="sm">
            <Stack sx={{}} direction="row" spacing={5} justifyContent="center">
              <Button
                type="submit"
                variant="contained"
                sx={{ bgcolor: "#82b1ff", width: "50%" }}
                onClick={handleSubmit(searchSubmit)}
              >
                検索
              </Button>
            </Stack>
          </Container>
        </Box>
        <Box
          sx={{
            ml: 2,
            mr: 2,
            mt: 4,
            alignItems: "center",
            justifyContent: "center"
          }}
          fullWidth
        >
          <div
            style={{
              height: 330,
              width: "100%",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <DataGrid
              sx={{
                bgcolor: "white",
                "& .headerColor": {
                  backgroundColor: "#ffcc80"
                }
              }}
              rows={gridData}
              columns={columns}
              onSelectionModelChange={handleSelectionModelChange}
              onCellEditCommit={handleGridDataChange}
              checkboxSelection
            />
          </div>
          <Container maxWidth="sm">
            <Stack sx={{ pt: 4 }} direction="row" spacing={5} justifyContent="center">
              <Button variant="contained" fullWidth sx={{ bgcolor: "#82b1ff" }} onClick={() => handleSubmitAction('update')}>
                変更
              </Button>
              <Button variant="contained" fullWidth sx={{ bgcolor: "#82b1ff" }} onClick={() => handleSubmitAction('stop')}>
                アカウント停止
              </Button>
            </Stack>
          </Container>
        </Box>

        <Box sx={{ alignItems: "right", justifyContent: "right", m: 2, display: "flex" }}>
          <Button variant="contained" size="medium" href="/AdminMenu" sx={{ bgcolor: "#82b1ff", marginLeft: "auto" }}>
            戻る
          </Button>
        </Box>
      </Container>
      <Footer />
    </Box>
  );
}